<template>
    <Teleport to="body" :disabled="!isMounted">
        <!--
            One panel for typing, styling and placement: the image above it is draggable throughout
            (see isOverlayInteractive in ProductMobileImageGallery), so there is no separate step to
            fold away into before the customer can drag, resize or rotate on the photo.
        -->
        <Transition
            enter-active-class="transition-transform duration-300 ease-out"
            enter-from-class="translate-y-full"
            leave-active-class="transition-transform duration-200 ease-in"
            leave-to-class="translate-y-full"
        >
            <!--
                Starts where the picture stops and takes the rest of the screen — the shot keeps its
                own square and is never resized for this, so what is left is the panel's. Its content
                scrolls inside it. See useAiTextEditorLayout.
            -->
            <section
                v-if="isStyling"
                class="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-2xl bg-white/99 shadow-[0_-4px_24px_rgba(0,0,0,0.18)] md:hidden"
                :style="editorPanelStyle"
                :aria-label="title"
                @focusin="onFocusIn"
                @focusout="onFocusOut"
            >
                <header class="flex shrink-0 items-center gap-2 border-b border-border px-4 py-3">
                    <TypeIcon class="size-4 shrink-0 text-primary" aria-hidden="true" />
                    <h2 class="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800" v-text="title" />
                    <button
                        type="button"
                        class="flex size-9 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:text-gray-600"
                        :aria-label="$t('pages.product_detail_page.ai_upload.personalization.view_photos')"
                        @click="onCancel"
                    >
                        <XIcon class="size-4" aria-hidden="true" />
                    </button>
                </header>

                <!--
                    flex-auto, not flex-1: the panel's own height is content-driven, and `flex-1`
                    zeroes an item's basis — with min-h-0 alongside it, the region between the header
                    and the footer had no size of its own to contribute. `auto` keeps the content's
                    size as the basis and still shrinks it once the panel hits its cap, which is
                    where this scroller takes over so the picture above never gives up more room.
                -->
                <!-- A little more room under the line while typing, so it does not sit on the button. -->
                <div class="min-h-0 flex-auto space-y-4 overflow-y-auto overscroll-contain px-4 py-3" :class="{ 'pb-6': isKeyboardOpen }">
                    <AiTextItemList variant="editor" />
                    <!--
                        Not while the keyboard is up. They cannot be reached past it, they are what
                        the save button's first job hands back — and the panel is measured, so the
                        room they take is room the picture above does not get. With them away the
                        panel is a header, a line and a button, and the shot being styled keeps the
                        rest of what is on screen.
                    -->
                    <TextStyleControls v-if="store.selectedItem !== undefined && !store.isItemBaked(store.selectedItem.id) && !isKeyboardOpen" />
                </div>

                <!--
                    One button, two jobs, in the order the customer meets them: with the keyboard up
                    it puts the typing away, which hands back the styling controls and this button's
                    own real purpose; with the keyboard down it commits. Saving straight from the
                    keyboard would skip the styling the panel exists for.
                -->
                <footer class="shrink-0 border-t border-border px-4 py-3">
                    <button
                        type="button"
                        class="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-x-1.5 rounded-md bg-order-buttons px-4 text-base font-extrabold text-white transition hover:brightness-110"
                        @click="isKeyboardOpen ? onTypingDone() : onSave()"
                    >
                        <CheckIcon class="size-5" aria-hidden="true" />
                        {{
                            isKeyboardOpen
                                ? $t('pages.product_detail_page.ai_upload.personalization.text_done')
                                : $t('pages.product_detail_page.ai_upload.personalization.mobile_save')
                        }}
                    </button>
                </footer>

                <!--
                    Carries the panel's own white on past its bottom edge. Whatever the browser
                    leaves between this panel and the keyboard it has just put up — and iOS does
                    leave a strip — is the page showing through, and this covers it rather than
                    trying to predict its height. 99% white for the same reason the order footer is:
                    Safari 26.0 does not paint a solid background on a fixed element into the bands
                    around its own chrome.
                -->
                <div class="pointer-events-none absolute inset-x-0 top-full h-[50vh] bg-white/99" aria-hidden="true" />
            </section>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { useAiEditorMode } from '@/composables/ai-upload/useAiEditorMode';
import { useAiEditorTyping } from '@/composables/ai-upload/useAiEditorTyping';
import { useAiSheetExpansion } from '@/composables/ai-upload/useAiSheetExpansion';
import { useAiTextEditorLayout } from '@/composables/ai-upload/useAiTextEditorLayout';
import { useKeyboardInset } from '@/composables/useKeyboardInset';
import AiTextItemList from '@/pages/ai-product-page/partials/ai-product-info/partials/AiTextItemList.vue';
import TextStyleControls from '@/pages/ai-product-page/partials/ai-product-info/partials/TextStyleControls.vue';
import { useAiPersonalizationStore } from '@/stores/ai-upload/AiPersonalizationStore';
import { useIsMounted } from '@/composables/useIsMounted';
import { CheckIcon, TypeIcon, XIcon } from '@lucide/vue';
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * The phone text editing surface: one panel for typing, styling and placement — the image above it
 * is draggable throughout, so there is no separate step to fold the panel away for. Only ever
 * mounted below the `md` breakpoint — above that the panel sits beside the gallery and does the
 * same job inline.
 */
const { t } = useI18n();
const store = useAiPersonalizationStore();
// Inertia's SSR pipeline drops Vue's teleport buffers, so teleported content
// is missing from the SSR HTML and hydration compares it against whatever
// <body>'s first child actually is. Stay disabled until mounted.
const isMounted = useIsMounted();
const { isStyling, isEditing, resetMode } = useAiEditorMode();
const { editorPanelStyle, measurePreviewBottom, forgetPreviewBottom } = useAiTextEditorLayout();
const { requestSheetExpansion } = useAiSheetExpansion();
const { keyboardInset } = useKeyboardInset();
const { isTyping, setTyping } = useAiEditorTyping();

/**
 * Either signal will do, and neither is enough on its own: the focus is the honest one, and the
 * measured inset covers a keyboard that came up without a field of ours being focused at all.
 */
const isKeyboardOpen = computed<boolean>(() => isTyping.value || keyboardInset.value > 0);

function isTextField(node: EventTarget | null): boolean {
    return node instanceof HTMLInputElement && node.type === 'text';
}

function onFocusIn(event: FocusEvent): void {
    setTyping(isTextField(event.target));
}

/**
 * relatedTarget is where the focus is going: moving between two lines is still typing, leaving for a
 * button or for nothing at all is the keyboard on its way down.
 *
 * Then checked again against the focus itself on the next turn of the loop, because relatedTarget is
 * not always filled in — Safari leaves it null for a tap that moves focus — and a stale "still
 * typing" costs the customer the styling controls and the picture.
 */
function onFocusOut(event: FocusEvent): void {
    setTyping(isTextField(event.relatedTarget));

    setTimeout(() => setTyping(isTextField(document.activeElement)), 0);
}

const title = computed<string>(() =>
    store.hasItems()
        ? t('pages.product_detail_page.ai_upload.personalization.title_update')
        : t('pages.product_detail_page.ai_upload.personalization.title'),
);

/** Puts the keyboard away without leaving the panel, so the styling controls come back into view. */
/**
 * Puts the keyboard away without leaving the panel, so the picture and the styling controls come
 * back.
 *
 * Says so itself rather than waiting to be told. iOS does not move focus to a button that is tapped,
 * so the only focusout is the one this blur causes — and the reported bug was the panel coming back
 * from the keyboard still in its typing state: no styling controls, and a button still offering to
 * put away typing that had already stopped.
 */
function onTypingDone(): void {
    if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
    }

    setTyping(false);
}

function scrollGalleryIntoView(): void {
    document.querySelector('[data-ai-gallery]')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
}

function closeEditor(): void {
    store.expandedId = undefined;
    store.isOpen = false;
    store.endTextEditingSession();
    resetMode();
}

/**
 * Backing out of the editor generates nothing, and keeps nothing either.
 *
 * The X is a cancel, so it puts the lines back the way they were when the editor was opened — which
 * for a first visit means the placeholder it seeded goes with them. It used to keep whatever had
 * been typed, uncommitted, on the reasoning that nothing the customer wrote should be thrown away;
 * but they closed without saving, and text surviving that reads as a save that did not say so.
 *
 * Text saved on an earlier visit is what the snapshot holds, so editing it and backing out hands
 * that version back rather than clearing the line.
 *
 * Still generates nothing: a generation spends one of the customer's daily credits, so it only ever
 * follows the save button. This is also what an unchanged save falls back to — see onSave.
 */
function onCancel(): void {
    store.discardTextEditingSession();
    closeEditor();
}

/**
 * Saving keeps the text and hands the screen back. It does not bake it.
 *
 * Only when the customer actually wrote something, though: the panel seeds a placeholder by itself,
 * and pressing save over nothing but that placeholder is not a confirmation of anything — it is the
 * reported bug, where tapping the button on an untouched field revealed the order button below. So an
 * unchanged field takes the exact same path as the X — see onCancel — rather than a copy of it, and
 * markTextSaved never runs.
 *
 * hasCustomerEdits, not a comparison against the placeholder string: it already tracks the same
 * thing — whether the line came from the customer rather than from the panel — and stays true through
 * a style-only change, which is also real customisation with nothing left to confirm about the text
 * itself.
 *
 * Baking waits for one of two things the customer does next: opening another beauty shot, which is
 * the point at which the text has to be on the artwork to be seen there, or ordering. Until then the
 * lines stay as the gallery's own overlay, which is live — so a customer who wants to change a
 * letter can, rather than having to take a baked name off the picture first.
 *
 * The gallery still goes to the top, because their text on the picture is what they just finished
 * writing, and the sheet still comes back up, since a customer who had collapsed it would otherwise
 * be handed back a screen with no way on.
 *
 * markTextSaved is what the footer's order button waits for: the text is theirs and confirmed, even
 * though it is not in the artwork yet.
 */
function onSave(): void {
    if (!store.hasCustomerEdits) {
        onCancel();

        return;
    }

    store.markTextSaved();
    closeEditor();
    scrollGalleryIntoView();
    requestSheetExpansion();
}

/**
 * Freezes the page where it stands, by taking the document out of the flow rather than by hiding its
 * overflow.
 *
 * overflow:hidden is not a lock on iOS: focusing a field there makes Safari scroll the document to
 * reveal it regardless, and the beauty shot went off the top of the screen the moment the keyboard
 * came up — the customer typing a name with no idea where it would sit. A body that is itself fixed,
 * offset by the scroll position it is standing at, has nothing left to scroll.
 */
let frozenScrollY = 0;
let settleFrame: number | undefined = undefined;

// Long enough for the browser's own smooth scroll from the far end of the page, short enough that a
// scroll it decided not to run does not leave the picture unaligned for a whole second.
const settleTimeoutMs = 1200;
// Frames of not moving, once it has moved at all, that count as arrived.
const settleFrames = 3;

function freezePageAt(scrollY: number): void {
    frozenScrollY = scrollY;

    document.body.style.position = 'fixed';
    document.body.style.top = `${-scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
}

function stopWatchingScroll(): void {
    if (settleFrame === undefined) {
        return;
    }

    cancelAnimationFrame(settleFrame);
    settleFrame = undefined;
}

function thawPage(): void {
    stopWatchingScroll();

    if (document.body.style.position !== 'fixed') {
        return;
    }

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';

    // The offset was standing in for the scroll while the body was fixed; hand it back.
    window.scrollTo(0, frozenScrollY);
}

function prefersReducedMotion(): boolean {
    if (typeof window.matchMedia !== 'function') {
        return false;
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Where the page has to stand for the gallery to start at the top of the screen. */
function galleryOffset(): number {
    const gallery = document.querySelector('[data-ai-gallery]')?.getBoundingClientRect();

    if (gallery === undefined) {
        return window.scrollY;
    }

    return Math.max(0, Math.round(window.scrollY + gallery.top));
}

/**
 * Freezes the page once its scroll has come to rest, and at the offset it was sent to either way.
 *
 * Watched rather than timed. The first go at this froze the page 400ms after starting a smooth
 * scroll, which from far enough down the page landed mid-animation and left the shot halfway up the
 * screen — a freeze cancels a scroll still in flight. Arrival, or three still frames after it has
 * moved at all, or a deadline: whichever comes first, and all three end at the target.
 */
function freezeWhenSettled(target: number): void {
    const deadline = performance.now() + settleTimeoutMs;
    let previous = window.scrollY;
    let hasMoved = false;
    let stillFrames = 0;

    const step = (): void => {
        settleFrame = undefined;

        if (!isEditing.value) {
            return;
        }

        const current = window.scrollY;

        hasMoved = hasMoved || current !== previous;
        stillFrames = current === previous ? stillFrames + 1 : 0;
        previous = current;

        const arrived = Math.abs(current - target) <= 1;
        const settled = hasMoved && stillFrames >= settleFrames;

        if (arrived || settled || performance.now() > deadline) {
            // At the target rather than wherever it got to: the offset is what holds the page from
            // here, so this is also the correction for a scroll that never ran.
            freezePageAt(target);

            return;
        }

        settleFrame = requestAnimationFrame(step);
    };

    settleFrame = requestAnimationFrame(step);
}

/**
 * Scrolls the page up to the gallery, and freezes it there.
 *
 * The browser's own smooth scroll, not an animation of ours: a transform on the body is what an
 * earlier go at this animated, and a transformed element is the containing block for every
 * `position: fixed` inside it — so the page carried the editor panel and the sheet up with it
 * instead of leaving them where they belong. `top` on a fixed body does not do that, but it repaints
 * the page every frame; scrollTo is the one the browser already knows how to run smoothly.
 */
function slideGalleryToTop(): void {
    const target = galleryOffset();

    stopWatchingScroll();

    if (prefersReducedMotion() || Math.abs(target - window.scrollY) <= 1) {
        freezePageAt(target);

        return;
    }

    window.scrollTo({ top: target, behavior: 'smooth' });
    freezeWhenSettled(target);
}

/**
 * The editor opens with the gallery sliding to the top of the screen, and the page frozen throughout.
 *
 * The panel's own top edge is measured before any of it, and from the picture's height rather than
 * its position: the gallery ends up at the top of the screen, so its height is where it ends — a
 * number that is already true while the slide is still running, and does not need measuring again
 * when it stops.
 */
watch(
    isEditing,
    (editing: boolean) => {
        if (!editing) {
            setTyping(false);
            thawPage();
            forgetPreviewBottom();

            return;
        }

        measurePreviewBottom();
        slideGalleryToTop();
    },
    // After the render: the gallery gives up part of its square while the editor is open, and the
    // measurement below is of that box. Taken before the resize it pinned the panel to where the
    // picture used to end.
    { flush: 'post' },
);

// Rotating the phone, or the browser's own chrome resizing, moves the edge the panel is pinned to.
function onViewportResize(): void {
    if (!isEditing.value) {
        return;
    }

    measurePreviewBottom();
}

onMounted(() => {
    window.addEventListener('resize', onViewportResize);
    window.addEventListener('orientationchange', onViewportResize);
});

/**
 * Text that has been baked into the artwork is no longer editable, and an empty list has nothing to
 * style — in both cases the editor has to let go rather than sit there over a dead selection.
 *
 * hasBakedText, not isCommitted: the latter also requires the items to match the committed snapshot,
 * and the overlay re-clamps every line whenever the print area changes — so simply opening another
 * thumbnail made baked text read as uncommitted, and a bake that landed while the editor was open
 * could leave it sitting there.
 *
 * Only a state change reaches here; being unable to open the editor over text that is already baked
 * is the panel's job, which offers the read-back row and its reset instead of a way in.
 *
 * Checks the raw item count rather than store.hasItems(), which is false while every line is blank:
 * clearing the input to retype a line must not slam the panel shut mid-edit.
 */
/*
 * Closes without discarding, unlike the X. Both states this fires on are the customer's own doing
 * and are the state they want kept: a bake that landed put their text into the artwork, and an empty
 * panel is every line deleted on purpose. Putting the opening snapshot back over either would undo
 * what they just did.
 */
watch(
    () => store.hasBakedText || store.items.length === 0,
    (shouldClose: boolean) => {
        if (shouldClose && isEditing.value) {
            closeEditor();
        }
    },
);

onUnmounted(() => {
    setTyping(false);
    window.removeEventListener('resize', onViewportResize);
    window.removeEventListener('orientationchange', onViewportResize);
    thawPage();
    forgetPreviewBottom();
    resetMode();
});
</script>
